import React, { useState } from 'react';
import validator from 'validator';
import { createStyles } from '@/contexts/theme-context';
import { TextInput, View } from 'react-native';
import { useReportCellFocus } from '@experiences/components/table/blocs/hooks/use-report-cell-focus';

interface EditableQuantityCellProps {
  localId: string;
  onUpdateQuantity: (localId: string, quantity: string) => void;
  quantity: string;
  rowIndex: number;
}

/**
 * Inline-editable quantity cell for the quote line-items table. Commits on
 * blur when the input parses as a positive number; otherwise resets to the
 * last-known good value. Reasonable defaults live in the reducer
 * (`ADD_CUSTOM_ITEM` seeds `quantity: '1'`), so zero is rejected to
 * prevent items that contribute nothing to the subtotal.
 */
export const EditableQuantityCell: React.FC<EditableQuantityCellProps> = ({
  localId,
  onUpdateQuantity,
  quantity,
  rowIndex,
}) => {
  const [styles, theme] = useStyles();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(quantity);
  const reportCellFocus = useReportCellFocus(rowIndex);

  const handleFocus = () => {
    reportCellFocus();
    setValue(quantity);
    setIsFocused(true);
  };

  // Match order-entry's quantity input — accept decimals + empty string
  // (transient state mid-edit); reject everything else at the keystroke.
  const handleChangeText = (text: string) => {
    if (text !== '' && !validator.isDecimal(text)) {
      return;
    }
    setValue(text);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseFloat(value);
    if (isNaN(parsed) || parsed <= 0) {
      setValue(quantity);
      return;
    }
    // Skip commit on a no-op blur so just tabbing through doesn't mark
    // the quote dirty.
    if (value === quantity) {
      return;
    }
    onUpdateQuantity(localId, value);
  };

  return (
    <View style={styles.container}>
      <TextInput
        keyboardType="decimal-pad"
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        placeholder="0"
        placeholderTextColor={theme.sk.color.text.tertiary}
        selectTextOnFocus
        style={[styles.input, isFocused && styles.inputFocused]}
        value={isFocused ? value : quantity}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    textAlign: 'center',
    width: '100%',
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
}));
